import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RetourVuePrecedente } from "@/lib/retourVue";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plane, MapPin, Lock, Loader2, Flag, BadgeCheck, Plus, ArrowUpRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ContactAnnonceur } from "@/components/ContactAnnonceur";
import { PartageAnnonce } from "@/components/PartageAnnonce";
import { PastilleEnLigne } from "@/components/PastilleEnLigne";
import { AIR_BENGUE, TYPES_AIR, libelleType } from "@/lib/airBengue";
import { estNouvelleAnnonce, BadgeNouveau } from "@/lib/annonces";
import { BadgesMedias } from "@/components/BadgesMedias";
import { getUser, formatFCFA } from "@/lib/user";
import { BandeauApercu } from "@/components/BandeauApercu";
import { useApercu, urlMedia } from "@/lib/apercu";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CarteAir = ({ annonce, profileId, floute }) => {
  const [signale, setSignale] = useState(false);
  const details = annonce.air || {};

  const signaler = async () => {
    const motif = window.prompt("Que souhaitez-vous signaler sur cette annonce ?");
    if (!motif) return;
    try {
      await axios.post(`${API}/air-bengue/annonces/${annonce.id}/signaler`, {
        profile_id: profileId, motif,
      });
      setSignale(true);
      toast.success("Signalement transmis", { description: "Notre équipe examine cette annonce." });
    } catch {
      toast.error("Signalement impossible pour le moment.");
    }
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      data-testid={`air-carte-${annonce.id}`}
      className="flex flex-col overflow-hidden rounded-3xl border border-[#E5DFD5] bg-white"
    >
      {annonce.photos?.[0] && (
        <div className="relative h-56 w-full overflow-hidden">
          <img
            src={urlMedia(annonce.photos[0].url)}
            alt={details.titre || "Annonce Air-Bengue"}
            loading="lazy"
            data-testid={`air-photo-${annonce.id}`}
            className={`h-full w-full object-cover transition-[filter] duration-500 ${
              floute ? "blur-[14px] scale-110" : ""}`}
          />
          {floute && (
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/25"
                  data-testid={`air-photo-verrou-${annonce.id}`}>
              <Lock size={18} className="text-white" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-white">Abonnez-vous pour voir</span>
            </span>
          )}
          {estNouvelleAnnonce(annonce.date_debut) && (
            <span className="absolute left-3 top-3">
              <BadgeNouveau testid={`air-badge-nouveau-${annonce.id}`} />
            </span>
          )}
          <BadgesMedias
            nbPhotos={annonce.nb_photos || annonce.photos?.length || 0}
            aVideo={annonce.a_video}
            testid={`air-medias-${annonce.id}`}
          />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading text-base font-bold leading-snug text-stone-900">
            {details.titre || "Prestation à l'étranger"}
          </h3>
          {details.disponible !== false && (
            <span className="shrink-0 rounded-full bg-[#0A5C36]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#0A5C36]">
              Dispo
            </span>
          )}
        </div>

        <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-stone-500">
          <MapPin size={13} /> {details.ville}
          {details.pays ? ` · ${details.pays}` : ""}
          <span className="text-stone-300">|</span>
          <span style={{ color: AIR_BENGUE.color }}>{libelleType(details.type_prestation)}</span>
        </p>

        {annonce.texte && (
          <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-stone-600">{annonce.texte}</p>
        )}

        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm font-bold text-stone-900" data-testid={`air-tarif-${annonce.id}`}>
            {details.tarif ? formatFCFA(details.tarif) : "Tarif sur demande"}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-500">
            <BadgeCheck size={13} style={{ color: AIR_BENGUE.color }} />
            {annonce.annonceur?.firstname}
            <PastilleEnLigne enLigne={annonce.annonceur?.en_ligne}
                             testid={`presence-${annonce.id}`} />
          </span>
        </div>

        {/* Aperçu aguicheur : le numéro reste masqué jusqu'à l'abonnement. */}
        {annonce.numero_masque && (
          <Link to="/abonnement" data-testid={`air-numero-masque-${annonce.id}`}
            className="mt-4 flex items-center justify-between gap-2 rounded-2xl border border-dashed border-[#E5DFD5] bg-[#FAF8F5] px-4 py-2.5">
            <span className="font-mono text-sm font-bold tracking-wide text-stone-700">
              {annonce.numero_masque}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider"
              style={{ color: AIR_BENGUE.color }}>
              <Lock size={11} /> Débloquer
            </span>
          </Link>
        )}

        <div className="mt-auto pt-4">
          {/* Fiche complète : galerie, texte intégral et contact de l'annonceur. */}
          <Link
            to={`/annonces/${annonce.id}`}
            data-testid={`air-voir-profil-${annonce.id}`}
            className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: AIR_BENGUE.color }}
          >
            Voir le profil <ArrowUpRight size={15} />
          </Link>
          <ContactAnnonceur annonceId={annonce.id} couleur={AIR_BENGUE.color} />
          <div className="mt-3 flex items-center justify-between gap-2">
            <PartageAnnonce annonceId={annonce.id} reference={annonce.slug} titre={details.titre} />
          </div>
          <button
            onClick={signaler}
            disabled={signale}
            data-testid={`air-signaler-${annonce.id}`}
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-stone-400 transition-colors hover:text-red-600 disabled:opacity-50"
          >
            <Flag size={11} /> {signale ? "Signalement envoyé" : "Signaler cette annonce"}
          </button>
        </div>
      </div>
    </motion.article>
  );
};

export default function AirBengue() {
  const user = getUser();
  const [annonces, setAnnonces] = useState(null);
  const [filtres, setFiltres] = useState({ villes: [], types: [] });
  const [ville, setVille] = useState("toutes");
  const [type, setType] = useState("tous");
  const [bloque, setBloque] = useState(null);
  const [premium, setPremium] = useState(false);
  const [visiteur, setVisiteur] = useState(false);
  const apercu = useApercu();
  const [photosNettes, setPhotosNettes] = useState(true);
  const [codeBloque, setCodeBloque] = useState(null);
  const [relance, setRelance] = useState(0);
  const floute = !photosNettes;
  const [params] = useSearchParams();

  useSeo({
    titre: `${AIR_BENGUE.name} — annonces de la diaspora${ville !== "toutes" ? ` à ${ville}` : ""}`,
    description:
      "Annonces Air-Bengue : rencontres et accompagnement pour la diaspora ivoirienne, " +
      "Paris et régions. Photos en clair, contact direct des annonceurs pour les abonnés.",
    chemin: "/air-bengue",
  });

  // Retour de paiement Air-Bengue : on relance la vérification jusqu'à l'activation.
  useEffect(() => {
    if (params.get("paye") !== "1" || premium) return;
    const attente = setTimeout(() => setRelance((n) => n + 1), 3000);
    return () => clearTimeout(attente);
  }, [params, premium]);

  useEffect(() => {
    axios.get(`${API}/air-bengue/villes`).then((r) => setFiltres(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setAnnonces(null);
    axios
      .get(`${API}/air-bengue/annonces`, {
        params: { viewer_id: user?.id, ville, type_prestation: type },
      })
      .then((r) => {
        setAnnonces(r.data.annonces);
        setPremium(r.data.numeros_debloques);
        setVisiteur(Boolean(r.data.visiteur));
        setPhotosNettes(Boolean(r.data.photos_nettes));
        setBloque(null);
        setCodeBloque(null);
      })
      .catch((err) => {
        setAnnonces([]);
        setPhotosNettes(true);
        setCodeBloque(err.response?.data?.detail?.code || null);
        setBloque(err.response?.data?.detail?.message || "Rubrique momentanément indisponible.");
      });
  }, [user?.id, ville, type, relance]);

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-6 lg:px-8" data-testid="air-bengue-page">
        {params.get("paye") === "1" && (
          <div className="mb-5">
            <RetourVuePrecedente libelle="Revenir à la prestation que je consultais" />
          </div>
        )}
        <header className="max-w-3xl">
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em]"
            style={{ background: AIR_BENGUE.colorSoft, color: AIR_BENGUE.color }}
          >
            <Plane size={13} className="-rotate-45" /> Nouveau
          </span>
          <h1 className="font-heading mt-5 text-4xl font-bold tracking-tight text-stone-900 sm:text-5xl lg:text-6xl">
            {AIR_BENGUE.name}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-stone-600">{AIR_BENGUE.description}</p>
        </header>

        {/* filtres */}
        <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="air-filtres">
          <select
            value={ville}
            onChange={(e) => setVille(e.target.value)}
            data-testid="air-filtre-ville"
            className="rounded-full border border-[#E5DFD5] bg-white px-4 py-2.5 text-sm font-semibold text-stone-800 outline-none"
          >
            <option value="toutes">Toutes les villes</option>
            {filtres.villes?.map((v) => (
              <option key={v.ville} value={v.ville}>{`${v.ville} (${v.nb})`}</option>
            ))}
          </select>
          {TYPES_AIR.map((t) => (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              data-testid={`air-filtre-type-${t.id}`}
              className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                type === t.id
                  ? "border-transparent text-white"
                  : "border-[#E5DFD5] bg-white text-stone-600 hover:border-stone-900"
              }`}
              style={type === t.id ? { background: AIR_BENGUE.color } : undefined}
            >
              {t.label}
            </button>
          ))}
          <Link
            to="/air-bengue/deposer"
            data-testid="air-deposer-link"
            className="ml-auto inline-flex items-center gap-2 rounded-full bg-stone-900 px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-stone-700"
          >
            <Plus size={14} /> Publier une annonce Bengué
          </Link>
        </div>

        {/* état d'accès */}
        {bloque && (
          <div
            className="mt-10 rounded-3xl border border-[#E5DFD5] bg-white p-8 text-center"
            data-testid="air-acces-bloque"
          >
            <Lock size={22} className="mx-auto" style={{ color: AIR_BENGUE.color }} />
            <h2 className="font-heading mt-4 text-base font-bold text-stone-900 md:text-lg">
              {codeBloque === "INSCRIPTION_REQUISE"
                ? "Créez votre compte gratuit"
                : "Rubrique réservée aux membres abonnés"}
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-stone-600">{bloque}</p>
            <Link
              to={codeBloque === "INSCRIPTION_REQUISE" ? "/inscription" : "/abonnement"}
              data-testid="air-vers-abonnement"
              className="mt-6 inline-flex rounded-full bg-[#E05638] px-6 py-3 text-sm font-bold text-white"
            >
              {codeBloque === "INSCRIPTION_REQUISE" ? "M'inscrire gratuitement" : "Voir les abonnements"}
            </Link>
          </div>
        )}

        {!bloque && visiteur && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl px-5 py-4"
            style={{ background: AIR_BENGUE.colorSoft }} data-testid="air-bandeau-visiteur">
            <p className="text-sm font-semibold" style={{ color: AIR_BENGUE.color }}>
              Vous voyez les photos et un aperçu des numéros. Créez votre compte gratuit pour
              tout voir, puis abonnez-vous pour appeler directement les annonceurs.
            </p>
            <Link to="/inscription" data-testid="air-visiteur-inscription"
              className="shrink-0 rounded-full bg-[#E05638] px-5 py-2.5 text-xs font-bold text-white">
              M'inscrire gratuitement
            </Link>
          </div>
        )}

        {!bloque && !visiteur && !premium && (
          <p
            className="mt-8 rounded-2xl border border-dashed border-[#E5DFD5] bg-white px-5 py-4 text-sm text-stone-600"
            data-testid="air-bandeau-premium"
          >
            Vous consultez les annonces. Les <strong>numéros de téléphone et WhatsApp</strong> sont
            réservés aux abonnés {AIR_BENGUE.name}.{" "}
            <Link to="/abonnement" className="font-bold text-[#E05638]">
              Débloquer les contacts
            </Link>
          </p>
        )}

        {annonces === null && !bloque && (
          <p className="mt-16 flex items-center gap-2 text-sm text-stone-400">
            <Loader2 size={15} className="animate-spin" /> Chargement des annonces…
          </p>
        )}

        {annonces?.length === 0 && !bloque && (
          <p className="mt-16 text-sm text-stone-500" data-testid="air-aucune-annonce">
            Aucune annonce pour ces critères. Revenez bientôt, la rubrique se remplit chaque jour.
          </p>
        )}

        {annonces?.length > 0 && (
          <div className="mt-10">
            <BandeauApercu apercu={apercu} categorie="air" />
          </div>
        )}

        {annonces?.length > 0 && (
          <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="air-liste">
            {annonces.map((a) => (
              <CarteAir key={a.id} annonce={a} profileId={user?.id} floute={floute} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
